import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Input } from '@redpanda/ui';
import FormFieldWithError from '../../components/forms/FormFieldWithError';
import { toast } from 'react-hot-toast';
import { customerAuthApi } from '../../services/api';

interface ResetPasswordFormData {
  newPassword: string;
  confirmPassword: string;
}

const ResetPasswordPage: React.FC = () => {
  const { t } = useTranslation(['pages', 'translation']);
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [submitting, setSubmitting] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<ResetPasswordFormData>();

  // 檢查是否有 token
  useEffect(() => {
    if (!token) {
      toast.error(t('pages:booking.resetPassword.missingToken'));
      setTimeout(() => {
        navigate(`/book/${storeId}/forgot-password`);
      }, 2000);
    }
  }, [token, navigate, storeId, t]);

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!token) return;

    try {
      setSubmitting(true);
      await customerAuthApi.resetPassword(token, data.newPassword);

      setResetSuccess(true);
      toast.success(t('pages:booking.resetPassword.resetSuccess'));

      // 3 秒後自動跳轉到登入頁
      setTimeout(() => {
        navigate(`/book/${storeId}/login`);
      }, 3000);
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('pages:booking.resetPassword.resetFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  // 密碼確認驗證
  const newPassword = watch('newPassword');

  if (resetSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 py-8 px-4">
        <div className="container mx-auto max-w-md">
          {/* 成功提示 */}
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md p-8 text-center">
            <div className="text-6xl mb-4">✅</div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              {t('pages:booking.resetPassword.successTitle')}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t('pages:booking.resetPassword.successMessage')}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500 mb-8">
              {t('pages:booking.resetPassword.autoRedirect')}
            </p>
            <button
              onClick={() => navigate(`/book/${storeId}/login`)}
              className="w-full px-6 py-3 bg-brand-600 hover:bg-brand-700 text-white rounded-lg font-bold transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              {t('pages:booking.resetPassword.loginNow')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">{t('pages:booking.resetPassword.verifying')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 py-8 px-4">
      <div className="container mx-auto max-w-md">
        {/* 返回按鈕 */}
        <button
          onClick={() => navigate(`/book/${storeId}/login`)}
          className="mb-6 flex items-center space-x-2 text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span>{t('pages:booking.resetPassword.backToLogin')}</span>
        </button>

        {/* 重設密碼卡片 */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md p-8">
          <div className="text-center mb-8">
            <div className="text-5xl mb-4">🔐</div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
              {t('pages:booking.resetPassword.title')}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {t('pages:booking.resetPassword.subtitle')}
            </p>
          </div>

          {/* 表單 */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* 新密碼 */}
            <FormFieldWithError
              label={t('pages:booking.resetPassword.newPassword')}
              required
              error={errors.newPassword?.message}
              htmlFor="newPassword"
            >
              <Input
                id="newPassword"
                type="password"
                placeholder={t('pages:booking.resetPassword.newPasswordPlaceholder')}
                {...register('newPassword', {
                  required: t('pages:booking.resetPassword.newPasswordRequired'),
                  minLength: {
                    value: 6,
                    message: t('translation:validation.passwordMinLength'),
                  },
                })}
                variant={errors.newPassword ? 'error' : 'default'}
                disabled={submitting}
              />
            </FormFieldWithError>

            {/* 確認密碼 */}
            <FormFieldWithError
              label={t('pages:booking.resetPassword.confirmPassword')}
              required
              error={errors.confirmPassword?.message}
              htmlFor="confirmPassword"
            >
              <Input
                id="confirmPassword"
                type="password"
                placeholder={t('pages:booking.resetPassword.confirmPasswordPlaceholder')}
                {...register('confirmPassword', {
                  required: t('pages:booking.resetPassword.confirmPasswordRequired'),
                  validate: (value) =>
                    value === newPassword || t('translation:validation.passwordMismatch'),
                })}
                variant={errors.confirmPassword ? 'error' : 'default'}
                disabled={submitting}
              />
            </FormFieldWithError>

            {/* 提交按鈕 */}
            <button
              type="submit"
              disabled={submitting}
              className={`
                w-full px-6 py-3 rounded-lg font-bold text-white transition-all duration-200
                ${
                  submitting
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-brand-600 hover:bg-brand-700 shadow-lg hover:shadow-xl'
                }
              `}
            >
              {submitting ? (
                <span className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>{t('pages:booking.resetPassword.resetting')}</span>
                </span>
              ) : (
                t('pages:booking.resetPassword.resetButton')
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
